import { useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Send, Sparkles } from "lucide-react"
import { api } from "@/lib/api"
import { searchHeroContacts } from "@/lib/heroSearch"
import { toast } from "@/components/portal/Toast"
import { notifyPendingSend, type PendingSendInfo } from "@/lib/undoSend"
import { platzhalterAufloesen } from "@/lib/signaturBilder"
import { newDraftId, removeDraft, saveDraft, type MailDraft } from "@/lib/draftModals"
import { mitTerminvorschlaegen } from "@/lib/terminvorschlaegeText"

/**
 * Neue Mail schreiben — Empfaenger, Betreff, Text, Anhaenge, KI-Entwurf.
 *
 * Aus pages/EmailsList.tsx herausgeloest (Schritt G). Der Dialog haengt an
 * nichts aus der Liste ausser dem Tenant-Slug, deshalb liess er sich am
 * saubersten herausschneiden.
 */

export function ComposeModal({
  open, onClose, tenantSlug, onSent, restoreDraft, postfachVorwahl = "",
}: {
  open: boolean
  onClose: () => void
  tenantSlug: string
  /** Im Posteingang gewähltes Postfach — Vorbelegung des Absender-Postfachs. */
  postfachVorwahl?: string
  onSent: () => void
  /** Wiederherzustellender Entwurf (aus der Entwurfsleiste unten rechts). */
  restoreDraft?: MailDraft | null
}) {
  const [to, setTo] = useState("")
  const [cc, setCc] = useState("")
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [prompt, setPrompt] = useState("")
  const [generating, setGenerating] = useState(false)
  const [sending, setSending] = useState(false)
  const [terminLaed, setTerminLaed] = useState(false)
  const [tenant, setTenant] = useState(tenantSlug || "")
  const [files, setFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  // Entwurf (#131): jede angefangene Mail steht als Entwurf in der Leiste
  // unten rechts und bleibt beim Schliessen, Seitenwechsel und Neuladen
  // erhalten. Nur Portal (localStorage), kein IMAP-Entwuerfe-Ordner (Paul,
  // 27.09.2026). Weg ist er erst nach dem Senden oder per „Verwerfen".
  const draftIdRef = useRef<string | null>(null)
  const draftCreatedRef = useRef<number>(0)

  // Signatur des GEWAEHLTEN Mandanten — der Absender laesst sich im Fenster
  // umstellen, die Vorschau muss mitgehen.
  const signaturDaten = useQuery<{
    signature_html?: string
    signature_logo_set?: boolean
    signature_images?: { name: string; hoehe?: number }[]
  }>({
    queryKey: ["tenant-signatur", tenant],
    queryFn: async () => {
      const r = await fetch(`/api/v1/tenants/${encodeURIComponent(tenant)}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: open && !!tenant,
    staleTime: 1000 * 60 * 5,
  }).data
  const signaturHtml = (signaturDaten?.signature_html || "").replace(/\n/g, "<br>")

  // Mehrere Postfaecher je Mandant: aus welchem geht die neue Mail raus?
  // Leer = Hauptpostfach. Die Auswahl erscheint nur, wenn es weitere gibt.
  // null = noch nicht selbst gewählt → Vorbelegung aus dem Posteingang.
  const [postfach, setPostfach] = useState<string | null>(null)
  const postfaecher = useQuery<{ haupt: { adresse: string }; weitere: { adresse: string; bezeichnung: string; aktiv: boolean }[] }>({
    queryKey: ["postfaecher", tenant],
    queryFn: async () => {
      const r = await fetch(`/api/v1/tenants/${encodeURIComponent(tenant)}/postfaecher`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: open && !!tenant,
    staleTime: 1000 * 60 * 5,
  }).data
  const weiterePostfaecher = (postfaecher?.weitere || []).filter((p) => p.aktiv)
  // Nach einem Mandantenwechsel gehoert die alte Auswahl nicht mehr dazu →
  // Hauptpostfach (statt eines Effects, der den Zustand nachzieht).
  const postfachRoh = postfach ?? postfachVorwahl
  const postfachGueltig = weiterePostfaecher.some((p) => p.adresse === postfachRoh) ? postfachRoh : ""

  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
    enabled: open,
  })

  // Beim Oeffnen IMMER den global gewaehlten Tenant uebernehmen.
  //
  // Frueher stand hier `&& !tenant` — also nur vorbelegen, wenn noch nichts
  // gewaehlt war. Das Modal bleibt aber montiert (`if (!open) return null`
  // statt Unmount), und nach dem Senden wird `tenant` nicht zurueckgesetzt.
  // Folge: Wer einmal einen Absender gewaehlt hatte, danach oben den Tenant
  // wechselte und erneut "Neue E-Mail" oeffnete, verschickte weiter unter dem
  // ALTEN Absender — sichtbar nur im kleinen Dropdown ganz oben im Dialog.
  // Genau so gingen am 06.08.2026 mehrere Mails an den falschen Zugang und
  // scheiterten dort still (pending_sends 7 und 8).
  //
  // `tenant` steht bewusst NICHT in den Dependencies: eine bewusste Auswahl
  // im offenen Dialog soll erhalten bleiben, nur das Oeffnen setzt zurueck.
  useEffect(() => {
    if (open && tenantSlug) setTenant(tenantSlug)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantSlug])

  // Entwurf wiederherstellen — der Absender kommt dann aus dem Entwurf, nicht
  // aus dem oben gewaehlten Mandanten. Steht bewusst NACH dem Effekt oben:
  // beide laufen beim Oeffnen, der spaetere gewinnt.
  useEffect(() => {
    if (!open || !restoreDraft) return
    draftIdRef.current = restoreDraft.id
    draftCreatedRef.current = restoreDraft.created_at
    setTenant(restoreDraft.tenant || tenantSlug || "")
    setTo(restoreDraft.to || "")
    setCc(restoreDraft.cc || "")
    setSubject(restoreDraft.subject || "")
    setBody(restoreDraft.body || "")
    setFiles([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, restoreDraft?.id])

  const hatInhalt = !!(to.trim() || cc.trim() || subject.trim() || body.trim())

  // Laufend mitschreiben (kurz entprellt), solange der Dialog offen ist.
  useEffect(() => {
    if (!open || !hatInhalt) return
    const t = setTimeout(() => entwurfSichern(), 800)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, to, cc, subject, body, tenant])

  function entwurfSichern() {
    if (!draftIdRef.current) {
      draftIdRef.current = newDraftId()
      draftCreatedRef.current = Date.now()
    }
    saveDraft({
      kind: "mail", id: draftIdRef.current, created_at: draftCreatedRef.current,
      tenant, to, cc, subject, body,
    })
  }

  function felderLeeren() {
    setTo(""); setCc(""); setSubject(""); setBody(""); setFiles([]); setPrompt(""); setPostfach(null)
    draftIdRef.current = null
  }

  /** Schliessen = als Entwurf ablegen, Dialog fuer die naechste Mail leeren. */
  const schliessen = () => {
    if (hatInhalt) {
      entwurfSichern()
      toast(files.length
        ? "Als Entwurf gespeichert — Anhänge bitte beim Weiterschreiben neu anfügen"
        : "Als Entwurf gespeichert — unten rechts weiterschreiben")
      felderLeeren()
    }
    setPostfach(null)
    onClose()
  }

  const verwerfen = () => {
    if (hatInhalt && !confirm("Entwurf verwerfen?")) return
    if (draftIdRef.current) removeDraft(draftIdRef.current)
    felderLeeren()
    onClose()
  }

  if (!open) return null

  const generateFromPrompt = async () => {
    if (!prompt.trim()) { toast("Prompt fehlt", "error"); return }
    if (!tenant) { toast("Bitte erst Absender wählen (für Signatur-Name)", "error"); return }
    setGenerating(true)
    try {
      const res = await fetch("/api/v1/emails/compose-ai", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, tenant_slug: tenant, recipient: to || null }),
      })
      const d = await res.json()
      if (!res.ok) throw new Error(d.detail || d.error || `HTTP ${res.status}`)
      setSubject(d.subject)
      setBody(d.body)
      toast("✓ E-Mail generiert")
    } catch (e) {
      toast("KI-Fehler: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setGenerating(false)
    }
  }

  // #200: freie Termine des Absenders als Text in die Nachricht — wie die
  // Terminvorschläge in einer Antwort. Liest nur: kein Blocker in HERO, kein
  // Versand; den Text prüft und schickt der Mensch.
  const terminvorschlaegeEinfuegen = async () => {
    if (!tenant) { toast("Bitte erst Absender wählen", "error"); return }
    setTerminLaed(true)
    try {
      const r = await fetch(`/api/v1/terminlinks/vorschlaege?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`)
      const labels: string[] = Array.isArray(d.vorschlaege) ? d.vorschlaege : []
      if (!labels.length) { toast("Keine freien Termine gefunden", "error"); return }
      setBody((bisher) => mitTerminvorschlaegen(bisher, labels))
      toast("✓ Terminvorschläge eingefügt — bitte prüfen")
    } catch (e) {
      toast("Terminvorschläge nicht ladbar: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setTerminLaed(false)
    }
  }

  const send = async () => {
    if (!tenant) { toast("Bitte Tenant (Absender) wählen", "error"); return }
    if (!to.trim()) { toast("Empfänger fehlt", "error"); return }
    if (!body.trim()) { toast("Nachricht fehlt", "error"); return }
    // Klarnamen statt Slug — "test_tenant" liest sich niemand durch, "Kunde
    // Testing" schon. Der Absender ist die Angabe, die hier am ehesten falsch
    // ist, also gehoert sie lesbar in die Rueckfrage.
    const absenderName =
      tenantsDd.data?.items?.find((t) => t.slug === tenant)?.name || tenant
    if (!confirm(`E-Mail von "${absenderName}" an "${to}" wirklich senden?`)) return
    setSending(true)
    try {
      const fd = new FormData()
      fd.append("tenant", tenant)
      if (postfachGueltig) fd.append("postfach", postfachGueltig)
      fd.append("to", to)
      fd.append("cc", cc)
      fd.append("subject", subject)
      fd.append("body", body)
      for (const f of files) fd.append("attachments", f)
      const r = await fetch("/api/v1/emails/compose", { method: "POST", credentials: "include", body: fd })
      const d = await r.json()
      if (d.ok) {
        // Phase 6.4 Soft-Send: Toast mit 30s-Stoppen statt sofortiger Bestaetigung
        try {
          notifyPendingSend(d as PendingSendInfo, `Mail an ${to}`)
        } catch { /* ignore */ }
        if (draftIdRef.current) removeDraft(draftIdRef.current)
        felderLeeren()
        onSent()
        onClose()
      } else {
        toast(d.error || "Senden fehlgeschlagen", "error")
      }
    } catch (e) {
      toast("Netzwerkfehler: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setSending(false)
    }
  }

  return (
    <div
      className="pp-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) schliessen()
      }}
    >
      <div
        onClick={(ev) => ev.stopPropagation()}
        style={{
          background: "var(--bg-card)",
          borderRadius: 12,
          width: "100%", maxWidth: 640, maxHeight: "90vh",
          boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border-color)",
                      display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Neue E-Mail</h3>
          <button type="button" onClick={schliessen} title="Schließen — angefangene Mail bleibt als Entwurf" style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--text-muted)" }}>×</button>
        </div>
        <div style={{ padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label className="pp-form-label">Absender (Tenant)</label>
            <select value={tenant} onChange={(e) => setTenant(e.target.value)}>
              <option value="">-- Bitte wählen --</option>
              {tenantsDd.data?.items?.map((t) => (
                <option key={t.slug} value={t.slug}>{t.name}</option>
              ))}
            </select>
          </div>
          {weiterePostfaecher.length > 0 && (
            <div>
              <label className="pp-form-label">Postfach</label>
              <select value={postfachGueltig} onChange={(e) => setPostfach(e.target.value)} data-testid="compose-postfach">
                <option value="">{postfaecher?.haupt.adresse || "Hauptpostfach"}</option>
                {weiterePostfaecher.map((p) => (
                  <option key={p.adresse} value={p.adresse}>{p.bezeichnung ? `${p.bezeichnung} (${p.adresse})` : p.adresse}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="pp-form-label">An</label>
            <EmpfaengerFeld
              value={to}
              onChange={setTo}
              tenant={tenant}
              placeholder="empfaenger@example.com"
            />
          </div>
          <div>
            <label className="pp-form-label">CC</label>
            <EmpfaengerFeld
              value={cc}
              onChange={setCc}
              tenant={tenant}
              placeholder="Optional — mehrere per Komma"
            />
          </div>
          <div>
            <label className="pp-form-label">Betreff</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          {/* KI-Prompt — generiert Betreff + Body aus Freitext */}
          <div style={{ padding: 10, background: "var(--accent-light)", borderRadius: 8, border: "1px solid rgba(99,102,241,0.2)" }}>
            <label className="pp-form-label" style={{ display: "flex", alignItems: "center", gap: 6, margin: 0, marginBottom: 6 }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--accent-color)" }} /> KI-Entwurf aus Prompt
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={2}
              placeholder="z.B. Termin am Di 14 Uhr bestätigen, Anfahrtskosten 45 €"
              style={{ resize: "vertical", width: "100%", fontSize: 12.5, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--border-color)", background: "var(--bg-card)" }}
            />
            <div style={{ marginTop: 6, display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={generateFromPrompt}
                disabled={generating || !prompt.trim()}
                className="pp-btn pp-btn-sm pp-btn-primary"
                style={{ fontSize: 12 }}
              >
                {generating ? "Generiert…" : (body ? "Neu generieren" : "Generieren")}
              </button>
            </div>
          </div>
          <div>
            <label className="pp-form-label">Nachricht</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} style={{ resize: "vertical" }} />
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                className="pp-btn pp-btn-sm"
                data-testid="compose-terminvorschlaege"
                onClick={terminvorschlaegeEinfuegen}
                disabled={terminLaed || !tenant}
                title="Fügt freie Termine als Text ein. Es wird nichts gebucht oder gesendet."
              >
                {terminLaed ? "Lädt…" : "Terminvorschläge einfügen"}
              </button>
            </div>
          </div>
          {/* Signatur-Vorschau (25.08.2026, „wo ist hier die signatur?").
              Sie WURDE immer angehaengt — `_send_email_for_kunde` holt sie
              selbst aus dem Mandanten —, war hier aber unsichtbar. Antworten
              und Weiterleiten zeigten sie laengst; nur „Neue E-Mail" nicht,
              und dann schreibt man sie aus Unsicherheit von Hand darunter. */}
          {signaturHtml && (
            <div>
              <label className="pp-form-label" style={{ color: "var(--text-muted)" }}>
                Signatur · wird automatisch angehängt
              </label>
              <div
                style={{
                  borderTop: "1px solid var(--border-color)", paddingTop: 8,
                  fontSize: 13, color: "var(--text-muted)", maxHeight: 150,
                  overflowY: "auto",
                }}
                dangerouslySetInnerHTML={{ __html: platzhalterAufloesen(signaturHtml, {
                  slug: tenant,
                  logoSet: signaturDaten?.signature_logo_set,
                  bilder: signaturDaten?.signature_images,
                }) }}
              />
            </div>
          )}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(ev) => {
                const list = Array.from(ev.target.files || [])
                setFiles((prev) => [...prev, ...list])
                if (fileInputRef.current) fileInputRef.current.value = ""
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="pp-btn pp-btn-sm"
              style={{ fontSize: 12 }}
            >
              📎 Anhang hinzufügen
            </button>
            {files.length > 0 && (
              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {files.map((f, i) => (
                  <span
                    key={i}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "var(--secondary-bg)", padding: "3px 8px",
                      borderRadius: 4, fontSize: 12,
                    }}
                  >
                    {f.name} <span style={{ color: "var(--text-muted)" }}>({Math.round(f.size / 1024)} KB)</span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 0 }}
                      title="Entfernen"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-color)",
                      display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={verwerfen} disabled={sending} className="pp-btn pp-btn-sm">
            Verwerfen
          </button>
          <button type="button" onClick={schliessen} disabled={sending} className="pp-btn pp-btn-sm">
            Als Entwurf speichern
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !tenant || !to.trim() || !body.trim()}
            className="pp-btn pp-btn-primary pp-btn-sm"
          >
            <Send className="w-3 h-3" /> {sending ? "Sende…" : "Senden"}
          </button>
        </div>
      </div>
    </div>
  )
}

type KontaktTreffer = {
  id: number
  first_name?: string | null
  last_name?: string | null
  company_name?: string | null
  email?: string | null
}

/** Letztes Adress-Bruchstueck einer Komma-Liste — danach wird gesucht. */
function letztesStueck(wert: string): string {
  return wert.split(",").pop()?.trim() ?? ""
}

/** Ersetzt das letzte Bruchstueck durch die gewaehlte Adresse. */
function stueckErsetzen(wert: string, adresse: string): string {
  const teile = wert.split(",")
  teile[teile.length - 1] = teile.length > 1 ? ` ${adresse}` : adresse
  return teile.join(",")
}

function anzeigeName(k: KontaktTreffer): string {
  const name = `${k.first_name || ""} ${k.last_name || ""}`.trim()
  return name || k.company_name || "(ohne Namen)"
}

/**
 * Empfaengerfeld mit Kontaktvorschlaegen (#17).
 *
 * Vorher war das ein nacktes Textfeld: wer schreiben wollte, musste die
 * Adresse auswendig koennen und fehlerfrei tippen — auch bei Kontakten, die
 * laengst im Portal stehen.
 *
 * #118 (Marlene, 24.09.2026): „woher ziehen sich eigentlich Vorschläge für
 * Email Adressen im Empfängerfeld? wir brauchen da alle aus Hero". Bis dahin
 * suchte das Feld nur im lokalen Kontakt-Cache — der ist ein Abzug, kein
 * HERO. Jetzt laeuft die Suche wie beim Weiterleiten LIVE gegen HERO
 * (`/api/v1/contacts/search`); der Cache bleibt Rueckfall, wenn HERO gerade
 * nicht antwortet. Ohne gewaehlten Absender gibt es keine Vorschlaege —
 * Adressen eines fremden Mandanten haben in dieser Liste nichts zu suchen.
 *
 * Das Feld bleibt ein Freitextfeld. Vorschlaege sind ein Angebot, keine
 * Pflicht — eine Adresse, die im Portal nicht steht, laesst sich weiter
 * einfach eintippen.
 */
function EmpfaengerFeld({
  value, onChange, tenant, placeholder,
}: {
  value: string
  onChange: (v: string) => void
  tenant: string
  placeholder?: string
}) {
  const [offen, setOffen] = useState(false)
  const [treffer, setTreffer] = useState<KontaktTreffer[]>([])
  const suche = letztesStueck(value)

  useEffect(() => {
    if (!offen || !tenant || suche.length < 2) { setTreffer([]); return }
    // Eine vollstaendige Adresse ist keine Suche mehr — sonst haengt die Liste
    // nach der Auswahl mit genau einem Treffer offen ueber dem Feld.
    if (suche.includes("@") && /\.[a-z]{2,}$/i.test(suche)) { setTreffer([]); return }
    let abgebrochen = false
    const t = setTimeout(async () => {
      try {
        let roh: KontaktTreffer[] = []
        try {
          // Live in HERO — dort stehen ALLE Kontakte, auch die von heute.
          roh = (await searchHeroContacts(tenant, suche)) as KontaktTreffer[]
        } catch {
          // HERO nicht erreichbar: der Cache ist besser als gar nichts.
          const { data } = await api.GET("/api/v1/contacts", {
            params: { query: { tenant, q: suche, limit: 8 } },
          })
          roh = (data?.items || []) as KontaktTreffer[]
        }
        if (abgebrochen) return
        const items = roh
          .filter((k) => (k.email || "").includes("@"))
        // Dieselbe Adresse steht im Cache oft mehrfach (Kontakt und
        // Ansprechpartner) — in einer Auswahlliste ist das nur Rauschen.
        const gesehen = new Set<string>()
        setTreffer(items.filter((k) => {
          const key = (k.email || "").toLowerCase()
          if (gesehen.has(key)) return false
          gesehen.add(key)
          return true
        }).slice(0, 8))
      } catch {
        if (!abgebrochen) setTreffer([])
      }
    }, 250)
    return () => { abgebrochen = true; clearTimeout(t) }
  }, [suche, tenant, offen])

  const waehlen = (k: KontaktTreffer) => {
    onChange(stueckErsetzen(value, k.email || ""))
    setTreffer([])
    setOffen(false)
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOffen(true) }}
        onFocus={() => setOffen(true)}
        // Erst nach dem Klick schliessen — `onBlur` feuert vor dem Klick auf
        // einen Vorschlag, sonst waere die Liste weg, bevor sie wirkt.
        onBlur={() => setTimeout(() => setOffen(false), 150)}
        onKeyDown={(e) => { if (e.key === "Escape") { setOffen(false); setTreffer([]) } }}
        autoComplete="off"
      />
      {offen && treffer.length > 0 && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
            marginTop: 2, maxHeight: 220, overflowY: "auto",
            background: "var(--bg-card)", border: "1px solid var(--border-color)",
            borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,.12)",
          }}
        >
          {treffer.map((k) => (
            <button
              key={k.id}
              type="button"
              // `onMouseDown` statt `onClick`: der Klick kommt erst nach dem
              // Blur des Feldes, `mousedown` davor.
              onMouseDown={(e) => { e.preventDefault(); waehlen(k) }}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "6px 10px", border: "none", background: "none",
                cursor: "pointer", fontSize: 12.5,
              }}
            >
              <div style={{ fontWeight: 500 }}>{anzeigeName(k)}</div>
              <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{k.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
